import { t } from 'i18next';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider } from 'core/states';
import { corporateUserService } from 'corporate/services';
import { RemoveUserButton } from 'projects/components/ProjectDetails';
import { useProjectUserActions } from 'projects/hooks';
import { IProjectUser, IProjectUserForm } from 'projects/types';
import { Size } from 'theme/enums';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  user?: IProjectUser;
}

const schema = () =>
  yup.object({
    corporateUser: yup.object().label(t('labelCorporateUser')).nullable().required(),
  });

export const UpsertUser = ({ isOpen, onClose, projectId, user }: IProps) => {
  const { t } = useTranslation();

  const { upsertProjectUser } = useProjectUserActions();

  const isEdit = useMemo(() => !!user?.id, [user]);

  const defaultValues = useMemo(() => {
    return {
      ...(user && { corporateUser: user }),
      customerEditAllowed: Boolean(user?.customerEditAllowed) ?? true,
      pricingEditAllowed: Boolean(user?.pricingEditAllowed),
      notificationsEnabled: Boolean(user?.notificationsEnabled),
    };
  }, [user]);

  const removeButton = useMemo(() => {
    return user ? <RemoveUserButton projectId={projectId} users={[user]} /> : null;
  }, [user, projectId]);

  const onSubmit = (reset: UseFormReset<IProjectUserForm>) => async (data: IProjectUserForm) => {
    const { success } = await upsertProjectUser({
      projectCorporateUser: {
        projectId,
        corporateUserId: data.corporateUser?.id,
        customerEditAllowed: data.customerEditAllowed ? 1 : 0,
        pricingEditAllowed: data.pricingEditAllowed ? 1 : 0,
        notificationsEnabled: data.notificationsEnabled ? 1 : 0,
      },
    });

    if (!success) {
      return;
    }

    setTimeout(() => {
      handleOnClose(reset);
    }, 1000);
  };

  const handleOnClose = (reset: UseFormReset<IProjectUserForm>) => {
    reset(defaultValues);
    onClose();
  };

  return (
    <FormProvider schema={schema()} defaultValues={defaultValues} resetDefaultValues>
      <Form
        isOpen={isOpen}
        onClose={handleOnClose}
        title={user?.id ? t('titleEditProjectUser') : t('titleAddProjectUser')}
        subtitle={user?.name}
        onSubmit={onSubmit}
        submitBtnLabel={isEdit ? t('buttonLabelUpdateProjectUser') : t('buttonLabelAddProjectUser')}
        extraButtons={removeButton}
      >
        {!isEdit && (
          <FormField
            name="corporateUser"
            type={InputType.AUTOCOMPLETE}
            wrapperStyle={{ flex: 1 }}
            inputProps={{
              placeholder: t('placeholderSelectUser'),
              getOptions: corporateUserService.getCorporateUsersAutocomplete,
              disableClearable: true,
            }}
          />
        )}

        <FormField
          name="customerEditAllowed"
          type={InputType.SWITCH}
          inputProps={{
            label: t('labelCustomerEditAllowed'),
            size: Size.MD,
          }}
        />

        <FormField
          name="pricingEditAllowed"
          type={InputType.SWITCH}
          inputProps={{
            label: t('labelPricingEditAllowed'),
            size: Size.MD,
          }}
        />

        <FormField
          name="notificationsEnabled"
          type={InputType.SWITCH}
          inputProps={{
            label: t('labelNotifications'),
            size: Size.MD,
          }}
        />
      </Form>
    </FormProvider>
  );
};
